export type Locality = { name: string; context: string; lat: number; lng: number };
type IndexedLocality = Locality & { normalizedNames: string[]; words: string[] };

const MAX_RESULTS = 6;

// "Forlì" -> "forli", "Reggio nell'Emilia" -> "reggio nell emilia", "Bolzano/Bozen" -> "bolzano bozen"
const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

let localitiesPromise: Promise<IndexedLocality[]> | null = null;

// A chunk of its own (~120 KB gzipped), requested only when somebody uses the search bar.
export const loadLocalities = () => {
  localitiesPromise ??= import('./localities.json').then(({ rows }) =>
    (rows as [string, string, number, number, string?][]).map(([name, context, lat, lng, otherName]) => {
      const normalizedNames = [name, otherName ?? ''].filter(Boolean).map(normalize);
      return { name, context, lat, lng, normalizedNames, words: normalizedNames.join(' ').split(' ') };
    })
  );
  return localitiesPromise;
};

/**
 * Every typed word must start a word of the name, so "reggio emilia" finds "Reggio nell'Emilia".
 * Names that start with the query come first; within each group the file order (population) holds.
 */
export const searchLocalities = (localities: IndexedLocality[], query: string, limit = MAX_RESULTS): Locality[] => {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return [];
  const queryWords = normalizedQuery.split(' ');
  const matches = localities.filter(({ words }) =>
    queryWords.every((queryWord) => words.some((word) => word.startsWith(queryWord)))
  );
  const startsWithQuery = ({ normalizedNames }: IndexedLocality) =>
    normalizedNames.some((normalizedName) => normalizedName.startsWith(normalizedQuery));
  const startingWithQuery = matches.filter(startsWithQuery);
  const others = matches.filter((locality) => !startsWithQuery(locality));
  return [...startingWithQuery, ...others].slice(0, limit);
};
